import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';

const PortfolioPage = () => {
  return (
    <Layout>
      <SEO
        title="Portfolio | Sandend House"
        description="Explore the operating companies, technology interests and long-term assets developed and stewarded through Sandend House."
        path="/portfolio"
        image="https://theknox.co.uk/og-portfolio.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Portfolio",
          "url": "https://theknox.co.uk/portfolio",
          "isPartOf": { "@id": "https://theknox.co.uk/#website" }
        }}
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <p className="eyebrow mb-4">Portfolio</p>
          <h1 className="font-serif text-4xl lg:text-5xl mb-8" style={{ maxWidth: '700px' }} data-testid="portfolio-heading">
            Businesses and interests built for the long term.
          </h1>
          <div className="reading-width">
            <p className="text-[var(--sh-charcoal)] text-lg leading-relaxed mb-4">
              Sandend House holds and stewards a focused portfolio of operating companies, intellectual property and long-term interests.
            </p>
            <p className="text-[var(--sh-grey)] leading-relaxed">
              The portfolio is built around sectors where technical capability, operational knowledge and retained ownership can create durable value.
            </p>
          </div>
        </div>
      </section>

      {/* The RLK Group */}
      <section className="py-20 lg:py-28 bg-white border-b border-[var(--sh-rule-light)]" data-testid="portfolio-rlk-section">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-8">
              <p className="eyebrow-brass mb-3">Advanced Technology</p>
              <h2 className="font-serif text-3xl lg:text-4xl mb-2">The RLK Group</h2>
              <p className="text-[var(--sh-grey)] text-sm mb-6">Founded by Calum Knox</p>
              <p className="text-[var(--sh-charcoal)] leading-relaxed mb-6">
                A UK technology group developing artificial intelligence, autonomous systems, defence software, simulation and operational analytics.
              </p>
              <p className="text-[var(--sh-grey)] leading-relaxed mb-8">
                The RLK Group operates through two principal companies, with several technology platforms and products developed within them.
              </p>

              {/* Sub-companies */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                <div className="border border-[var(--sh-rule)] rounded p-6">
                  <h3 className="font-serif text-lg mb-2">RLK Technologies</h3>
                  <p className="text-[var(--sh-grey)] text-sm leading-relaxed">
                    AI-driven platforms for operational analytics, urban intelligence and commercial applications.
                  </p>
                  <p className="text-[var(--sh-grey)] text-xs mt-3">Platforms: UrbanAnalytix</p>
                </div>
                <div className="border border-[var(--sh-rule)] rounded p-6">
                  <h3 className="font-serif text-lg mb-2">RLK Defence</h3>
                  <p className="text-[var(--sh-grey)] text-sm leading-relaxed">
                    Autonomous systems, counter-UAS solutions and defence software for contested environments.
                  </p>
                  <p className="text-[var(--sh-grey)] text-xs mt-3">Platforms: AI-AV, Helais, UAVSimNet</p>
                </div>
              </div>

              <a href="https://therlkgroup.com" target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="visit-rlk">
                Visit The RLK Group <ArrowUpRight size={16} className="ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Letwise */}
      <section className="py-20 lg:py-28 border-b border-[var(--sh-rule-light)]" data-testid="portfolio-letwise-section">
        <div className="section-container">
          <div className="lg:max-w-3xl">
            <p className="eyebrow-brass mb-3">Property Technology</p>
            <h2 className="font-serif text-3xl lg:text-4xl mb-2">Letwise</h2>
            <p className="text-[var(--sh-grey)] text-sm mb-6">Founded by Calum Knox</p>
            <p className="text-[var(--sh-charcoal)] leading-relaxed mb-8">
              A property technology and lettings platform designed to reduce management friction, improve transparency and automate routine property-management processes.
            </p>
            <a href="https://letwise.io" target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="visit-letwise">
              Visit Letwise <ArrowUpRight size={16} className="ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20">
        <div className="section-container flex flex-wrap gap-4">
          <Link to="/intellectual-property" className="btn-primary">
            Explore the IP Portfolio <ArrowRight size={16} className="ml-2" />
          </Link>
          <Link to="/founder" className="btn-secondary">
            Meet the Founder <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default PortfolioPage;
